import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { getFirstOrderTags, getSecondOrderTags, getThirdOrderTags } from '../data/tagHierarchy';

const SmartTagsInput = ({ 
  tags = [], 
  onTagsChange, 
  selectedHobbies = [],
  locationContext = null,
  label = 'Tags',
  required = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [suggestionsGroupedByCategory, setSuggestionsGroupedByCategory] = useState([]); // { category, tags }[]
  const [inputValue, setInputValue] = useState('');
  
  const isRainyCity = (city) => {
    if (!city) return false;
    const rainyCities = new Set(['seattle', 'bellevue', 'redmond', 'tacoma', 'everett', 'portland']);
    return rainyCities.has(String(city).toLowerCase().trim());
  };

  const getSecondTagSuggestions = (hobby, firstTag) => {
    const base = getSecondOrderTags(hobby, firstTag) || [];
    const city = locationContext?.city || '';
    const rainy = isRainyCity(city);

    // Sports: nudge Indoor/Outdoor based on rainy cities
    if (String(hobby || '').toLowerCase() === 'sports' && String(firstTag || '').toLowerCase() === 'basketball') {
      const normalized = Array.from(new Set(['Indoor', 'Outdoor', ...base]));
      return rainy
        ? ['Indoor', ...normalized.filter(t => t !== 'Indoor')]
        : ['Outdoor', ...normalized.filter(t => t !== 'Outdoor')];
    }

    // Spirituality: if user picked a place-of-worship type, suggest meetup formats
    if (String(hobby || '').toLowerCase() === 'spirituality') {
      const defaults = ['Community', 'House Meetups', 'Student Fellowship', 'Weekly Service', 'Monthly Gathering'];
      return Array.from(new Set([...base, ...defaults]));
    }

    // If no 2nd-order mapping (e.g. club categories like Arts & Culture), suggest other 1st-order tags so user can pick 2nd and 3rd
    if (base.length === 0) {
      const firstOrder = getFirstOrderTags(hobby) || [];
      return firstOrder.filter(t => t !== firstTag);
    }

    return base;
  };

  // Which category (from selectedHobbies) does this tag belong to?
  const getCategoryForTag = (tag) => {
    if (!tag) return null;
    for (const hobby of selectedHobbies) {
      const firstOrder = getFirstOrderTags(hobby) || [];
      if (firstOrder.includes(tag)) return hobby;
    }
    return null;
  };

  // Build suggestions in category order: all tags from 1st hobby, then 2nd, then 3rd (no duplicates)
  const buildSuggestionsInCategoryOrder = (excludeTags = []) => {
    const excludeSet = new Set(excludeTags);
    const seen = new Set();
    const out = [];
    for (const hobby of selectedHobbies) {
      const firstOrder = getFirstOrderTags(hobby) || [];
      for (const t of firstOrder) {
        if (!excludeSet.has(t) && !seen.has(t)) {
          seen.add(t);
          out.push(t);
        }
      }
    }
    return out;
  };

  // Determine which suggestions to show: always in category order (all from 1st, then 2nd, then 3rd)
  useEffect(() => {
    if (tags.length >= 3) {
      setCurrentSuggestions([]);
      setSuggestionsGroupedByCategory([]);
      setIsOpen(false);
      return;
    }

    if (selectedHobbies.length === 0) {
      setCurrentSuggestions([]);
      setSuggestionsGroupedByCategory([]);
      setIsOpen(false);
      return;
    }

    if (tags.length === 0) {
      // 1st tag: all tags from each category, grouped
      const grouped = selectedHobbies
        .map((hobby) => {
          const tagList = (getFirstOrderTags(hobby) || []).filter((t) => !tags.includes(t));
          return { category: hobby, tags: tagList };
        })
        .filter((g) => g.tags.length > 0);
      setSuggestionsGroupedByCategory(grouped);
      setCurrentSuggestions(grouped.flatMap((g) => g.tags));
      setIsOpen(false);
      return;
    }

    if (tags.length === 1) {
      // 2nd tag: for the category that has tags[0], show 2nd-order or remaining 1st-order; then all from other categories in order
      const categoryOfFirst = getCategoryForTag(tags[0]);
      const grouped = [];
      const excludeSet = new Set(tags);
      for (const hobby of selectedHobbies) {
        let tagList;
        if (hobby === categoryOfFirst) {
          tagList = getSecondTagSuggestions(hobby, tags[0]).filter((t) => !excludeSet.has(t));
        } else {
          tagList = (getFirstOrderTags(hobby) || []).filter((t) => !excludeSet.has(t));
        }
        if (tagList.length > 0) grouped.push({ category: hobby, tags: tagList });
      }
      setSuggestionsGroupedByCategory(grouped);
      setCurrentSuggestions(grouped.flatMap((g) => g.tags));
      setIsOpen(grouped.length > 0);
      return;
    }

    // tags.length === 2 => 3rd tag: third-order suggestions based on 2nd tag (e.g. Road Running -> Weekly, 5K, etc.)
    const categoryOfFirst = getCategoryForTag(tags[0]);
    const thirdOrderTags = categoryOfFirst
      ? getThirdOrderTags(categoryOfFirst, tags[0], tags[1])
      : [];
    if (thirdOrderTags.length > 0) {
      const grouped = [{ category: categoryOfFirst, tags: thirdOrderTags.filter((t) => !tags.includes(t)) }];
      const filtered = grouped.filter((g) => g.tags.length > 0);
      if (filtered.length > 0) {
        setSuggestionsGroupedByCategory(filtered);
        setCurrentSuggestions(filtered.flatMap((g) => g.tags));
        setIsOpen(true);
        return;
      }
    }
    // Fallback: remaining first-order tags from each category
    const grouped = selectedHobbies
      .map((hobby) => {
        const tagList = (getFirstOrderTags(hobby) || []).filter((t) => !tags.includes(t));
        return { category: hobby, tags: tagList };
      })
      .filter((g) => g.tags.length > 0);
    setSuggestionsGroupedByCategory(grouped);
    setCurrentSuggestions(grouped.flatMap((g) => g.tags));
    setIsOpen(grouped.length > 0);
  }, [tags, JSON.stringify(selectedHobbies), JSON.stringify(locationContext)]);

  const handleAddTagFromSuggestion = (tag) => {
    const maxTags = required ? 3 : 10;
    if (tags.length >= maxTags) return;
    if (tags.includes(tag)) return;

    if (tags.length === 0) {
      onTagsChange([tag]);
      setInputValue('');
      setIsOpen(false);
      return;
    }
    if (tags.length === 1) {
      onTagsChange([...tags, tag]);
      setInputValue('');
      setIsOpen(false);
      return;
    }
    if (tags.length === 2) {
      onTagsChange([...tags, tag]);
      setInputValue('');
      setIsOpen(false);
      return;
    }
  };

  const handleAddTagFromInput = (e) => {
    e.preventDefault();
    const tag = inputValue.trim();
    const maxTags = required ? 3 : 10; // Allow more tags if not required
    if (tag && !tags.includes(tag) && tags.length < maxTags) {
      onTagsChange([...tags, tag]);
      setInputValue('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    onTagsChange(newTags);
    if (newTags.length === 0 && selectedHobbies.length > 0) {
      setCurrentSuggestions(buildSuggestionsInCategoryOrder([]));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (tags.length <= 2 && isOpen && currentSuggestions.length > 0) {
        // Don't submit if dropdown is open with suggestions
        return;
      }
      handleAddTagFromInput(e);
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      handleRemoveTag(tags[tags.length - 1]);
    }
  };

  const getPlaceholder = () => {
    if (tags.length === 0) {
      if (currentSuggestions.length > 0) {
        return 'Click to see recommendations or type your own';
      }
      return 'Type a tag';
    }
    if (required) {
      if (tags.length === 1) {
        if (currentSuggestions.length > 0) return 'Click to see 2nd tag recommendations or type your own';
        return 'Type 2nd tag';
      }
      if (tags.length === 2) {
        if (currentSuggestions.length > 0) return 'Click to see 3rd tag recommendations or type your own';
        return 'Type 3rd tag';
      }
      return 'All 3 tags selected';
    }
    return 'Add another tag';
  };

  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-gray-900 dark:text-[#e5e5e5] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
        {tags.length === 0 && (
          <span className="ml-2 text-[10px] text-gray-600 dark:text-gray-300 font-normal">
            (1st tag: select from suggestions)
          </span>
        )}
        {tags.length === 1 && (
          <span className="ml-2 text-[10px] text-gray-600 dark:text-gray-300 font-normal">
            (2nd tag: suggested from hobby + location)
          </span>
        )}
        {tags.length === 2 && required && (
          <span className="ml-2 text-[10px] text-gray-600 dark:text-gray-300 font-normal">
            (3rd tag: select from suggestions or type your own)
          </span>
        )}
      </label>
      
      {/* Tags Display - optional (required=false) uses dark input bg in dark mode to match recommendations layout */}
      <div className="flex flex-wrap gap-1 min-h-[40px] p-2.5 border border-gray-300 dark:border-[#3d3d3d] rounded-xl bg-white dark:bg-[#252525] text-gray-900 dark:text-[#e5e5e5] focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500/50 transition-all shadow-sm hover:shadow-md">
        {tags.map((tag, index) => (
          <span
            key={tag}
            className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-gray-900 dark:bg-gray-700 text-white text-[12px] rounded-full"
          >
            <span className="text-[10px] mr-0.5">{index + 1}.</span>
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="hover:bg-gray-800 dark:hover:bg-gray-600 rounded-full p-0.5 transition-colors"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        
        {tags.length < (required ? 3 : 10) && (
          <div className="flex items-center flex-1 min-w-[200px]">
            {tags.length <= 2 && currentSuggestions.length > 0 ? (
              // 1st and 2nd tag: Show dropdown with suggestions
              <div className="relative w-full">
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tags.length <= 2 && currentSuggestions.length > 0 && !inputValue.trim()) {
                      setIsOpen(!isOpen);
                    }
                  }}
                  className={`flex items-center justify-between w-full ${currentSuggestions.length > 0 ? 'cursor-pointer' : ''}`}
                >
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      if (e.target.value.trim()) {
                        setIsOpen(false);
                      } else if (currentSuggestions.length > 0) {
                        setIsOpen(true);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                      e.stopPropagation();
                      if (currentSuggestions.length > 0 && !inputValue.trim()) {
                        setIsOpen(true);
                      }
                    }}
                    placeholder={getPlaceholder()}
                    className="flex-1 px-1.5 py-0.5 text-[14px] font-medium border-none outline-none bg-transparent text-gray-900 dark:text-[#e5e5e5] placeholder-gray-600 dark:placeholder-gray-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentSuggestions.length > 0 && !inputValue.trim()) {
                        setIsOpen(true);
                      }
                    }}
                  />
                  {currentSuggestions.length > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-gray-500 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/40 px-1.5 py-0.5 rounded">
                        {currentSuggestions.length} suggestions
                      </span>
                      {isOpen ? (
                        <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      )}
                    </div>
                  )}
                </div>
                
                {/* Dropdown Suggestions for 1st, 2nd, and 3rd tag - grouped by category */}
                {isOpen && tags.length <= 2 && currentSuggestions.length > 0 && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setIsOpen(false)}
                    />
                    <div className="absolute z-20 w-full mt-1.5 bg-white/95 dark:bg-[#181818] backdrop-blur-md rounded-xl border-2 border-white/20 dark:border-[#2d2d2d] shadow-xl max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-white/20 dark:border-[#2d2d2d] bg-blue-50/50 dark:bg-blue-900/20">
                        <p className="text-[11px] font-semibold text-gray-900 dark:text-[#e5e5e5]">
                          {tags.length === 0
                            ? (selectedHobbies.length === 1
                                ? `Tags for "${selectedHobbies[0]}"`
                                : 'Select a tag')
                            : tags.length === 1
                              ? `Recommended 2nd tag (${locationContext?.city || locationContext?.state || 'your area'})`
                              : `Recommended 3rd tag`}
                        </p>
                        <p className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">
                          Click to select or type your own
                        </p>
                      </div>
                      <div className="py-1">
                        {suggestionsGroupedByCategory.map(({ category, tags: tagList }) => (
                          <div key={category} className="border-b border-gray-100 dark:border-[#2d2d2d] last:border-b-0">
                            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#1f1f1f] sticky top-0">
                              {category}
                            </div>
                            {tagList.map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                onClick={() => {
                                  handleAddTagFromSuggestion(suggestion);
                                }}
                                className="w-full text-left pl-5 pr-3 py-2 text-[14px] hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors text-gray-900 dark:text-gray-100"
                              >
                                <span className="font-medium">{suggestion}</span>
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              // 2nd and 3rd tags: Free-form input
              <form onSubmit={handleAddTagFromInput} className="flex items-center flex-1">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholder()}
                  className="flex-1 px-1.5 py-0.5 text-[13px] border-none outline-none bg-transparent min-w-[120px] text-gray-900 dark:text-[#e5e5e5] placeholder-gray-500 dark:placeholder-gray-400"
                />
                {inputValue.trim() && (
                  <button
                    type="submit"
                    className="ml-0.5 p-0.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                )}
              </form>
            )}
          </div>
        )}
      </div>

      {/* Helper Text */}
      {tags.length === 0 && selectedHobbies.length === 0 && (
        <p className="text-[11px] text-amber-600 mt-1">
          Please select a hobby first to see tag suggestions for the 1st tag
        </p>
      )}
      {(tags.length === 0 || tags.length === 1 || tags.length === 2) && selectedHobbies.length > 0 && currentSuggestions.length > 0 && !isOpen && (
        <p className="text-[11px] text-blue-600 mt-1">
          💡 {currentSuggestions.length} tag suggestions available - click the input to see them
        </p>
      )}
      {tags.length === 0 && selectedHobbies.length > 0 && currentSuggestions.length === 0 && (
        <p className="text-[11px] text-gray-500 mt-1">
          No suggestions for selected categories — type your own tag
        </p>
      )}
      {required && tags.length < 3 && tags.length > 0 && (
        <p className="text-[11px] text-gray-500 mt-1">
          {3 - tags.length} more tag{3 - tags.length > 1 ? 's' : ''} required (type your own)
        </p>
      )}
      {required && tags.length === 3 && (
        <p className="text-[11px] text-green-600 mt-1">
          ✓ All 3 tags selected
        </p>
      )}
    </div>
  );
};

export default SmartTagsInput;
